/**
 * SKOP Social Sentiment
 *
 * Aggregate and display social media sentiment for tokens.
 */

export type SentimentTrend = 'improving' | 'stable' | 'declining';

export interface SocialMention {
  id: string;
  source: 'twitter' | 'telegram' | 'discord';
  text: string;
  author: string;
  authorHandle?: string;
  url?: string;
  sentiment: number; // -100 to +100
  timestamp: number;
  engagement: {
    likes: number;
    retweets: number;
    replies: number;
  };
}

export interface TokenSentiment {
  tokenAddress: string;
  overallScore: number; // -100 to +100
  trend: SentimentTrend;
  confidence: 'high' | 'medium' | 'low';
  mentionCount24h: number;
  mentionCountChange: number; // % change vs previous 24h
  topKeywords: string[];
  topHashtags: string[];
  recentMentions: SocialMention[];
  breakdown: {
    positive: number;
    neutral: number;
    negative: number;
  };
  historicalScores: Array<{
    timestamp: number;
    score: number;
  }>;
  lastUpdated: number;
}

// Cache configuration
const SENTIMENT_CACHE_KEY = 'skop_sentiment_cache';
const SENTIMENT_CACHE_DURATION = 15 * 60 * 1000; // 15 minutes

interface CachedSentiment {
  sentiment: TokenSentiment;
  cachedAt: number;
}

// ============================================================================
// Sentiment Functions
// ============================================================================

/**
 * Get sentiment for a token
 */
export async function getTokenSentiment(tokenAddress: string): Promise<TokenSentiment> {
  // Check cache first
  const cached = getCachedSentiment(tokenAddress);
  if (cached) {
    return cached;
  }

  // Generate mock sentiment (in production, would call social API)
  const sentiment = await generateMockSentiment(tokenAddress);

  // Cache result
  cacheSentiment(tokenAddress, sentiment);

  return sentiment;
}

/**
 * Generate mock sentiment data
 */
async function generateMockSentiment(tokenAddress: string): Promise<TokenSentiment> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 600));

  // Generate consistent data based on token address
  const seed = hashCode(tokenAddress);
  const random = seededRandom(seed);

  // Overall score (-100 to +100)
  const overallScore = Math.floor(random() * 200) - 100;

  // Trend based on score and randomness
  const trendRandom = random();
  const trend: SentimentTrend = trendRandom > 0.6 ? 'improving' :
    trendRandom > 0.3 ? 'stable' : 'declining';

  // Confidence based on mention count
  const mentionCount24h = Math.floor(random() * 500) + 10;
  const confidence: 'high' | 'medium' | 'low' =
    mentionCount24h > 200 ? 'high' :
    mentionCount24h > 50 ? 'medium' : 'low';

  // Mention count change
  const mentionCountChange = Math.floor(random() * 300) - 50; // -50% to +250%

  // Keywords and hashtags
  const allKeywords = ['bullish', 'mooning', 'gem', 'degen', 'lfg', 'rug', 'scam', 'pump', 'dump', 'hold'];
  const allHashtags = ['#solana', '#memecoin', '#crypto', '#defi', '#nfa', '#dyor', '#alpha', '#wagmi'];

  const topKeywords = shuffleArray([...allKeywords], random).slice(0, 5);
  const topHashtags = shuffleArray([...allHashtags], random).slice(0, 4);

  // Breakdown
  const positive = Math.floor(random() * 60) + (overallScore > 0 ? 20 : 5);
  const negative = Math.floor(random() * 40) + (overallScore < 0 ? 20 : 5);
  const neutral = 100 - positive - negative;

  // Generate recent mentions
  const recentMentions = generateMockMentions(random, Math.min(mentionCount24h, 10));

  // Generate historical scores (last 7 days)
  const historicalScores = generateHistoricalScores(overallScore, trend, random);

  return {
    tokenAddress,
    overallScore,
    trend,
    confidence,
    mentionCount24h,
    mentionCountChange,
    topKeywords,
    topHashtags,
    recentMentions,
    breakdown: { positive, neutral, negative },
    historicalScores,
    lastUpdated: Date.now(),
  };
}

/**
 * Generate mock social mentions
 */
function generateMockMentions(random: () => number, count: number): SocialMention[] {
  const sources: SocialMention['source'][] = ['twitter', 'telegram', 'discord'];
  const authors = [
    { name: 'CryptoWhale', handle: '@cryptowhale' },
    { name: 'SolanaAlpha', handle: '@solanaalpha' },
    { name: 'DegenTrader', handle: '@degentrader' },
    { name: 'MemeKing', handle: '@memeking' },
    { name: 'TokenScout', handle: '@tokenscout' },
    { name: 'ChartMaster', handle: '@chartmaster' },
    { name: 'DiamondHands', handle: '@diamondhands' },
    { name: 'ApeInto', handle: '@apeinto' },
  ];

  const positiveTexts = [
    'This one is looking bullish! NFA but I\'m loading up',
    'Chart looking clean, volume picking up nicely',
    'Early gem alert! Community is strong',
    'LFG! This could be the next 100x',
    'Dev is based, community is active. Bullish!',
  ];

  const neutralTexts = [
    'Watching this one closely, interesting project',
    'Anyone else tracking this? Thoughts?',
    'Volume seems decent, need to see more action',
    'On my watchlist but not aping yet',
    'Interesting tokenomics, doing more research',
  ];

  const negativeTexts = [
    'Be careful with this one, looks suspicious',
    'Dev wallet moved funds, could be rug incoming',
    'Holder distribution looks concerning',
    'Volume looks fake, mostly wash trading',
    'This screams honeypot, DYOR',
  ];

  const mentions: SocialMention[] = [];
  for (let i = 0; i < count; i++) {
    const sentiment = Math.floor(random() * 200) - 100;
    const author = authors[Math.floor(random() * authors.length)];
    const source = sources[Math.floor(random() * sources.length)];

    let text: string;
    if (sentiment > 30) {
      text = positiveTexts[Math.floor(random() * positiveTexts.length)];
    } else if (sentiment < -30) {
      text = negativeTexts[Math.floor(random() * negativeTexts.length)];
    } else {
      text = neutralTexts[Math.floor(random() * neutralTexts.length)];
    }

    mentions.push({
      id: `mention_${i}_${Date.now()}`,
      source,
      text,
      author: author.name,
      authorHandle: author.handle,
      url: source === 'twitter' ? `https://twitter.com/${author.handle.slice(1)}/status/123456789` : undefined,
      sentiment,
      timestamp: Date.now() - Math.floor(random() * 24 * 60 * 60 * 1000), // Within last 24h
      engagement: {
        likes: Math.floor(random() * 500),
        retweets: Math.floor(random() * 100),
        replies: Math.floor(random() * 50),
      },
    });
  }

  // Sort by timestamp (most recent first)
  return mentions.sort((a, b) => b.timestamp - a.timestamp);
}

/**
 * Generate historical sentiment scores
 */
function generateHistoricalScores(
  currentScore: number,
  trend: SentimentTrend,
  random: () => number
): Array<{ timestamp: number; score: number }> {
  const scores: Array<{ timestamp: number; score: number }> = [];
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;

  let score = currentScore;
  for (let i = 0; i < 7; i++) {
    scores.unshift({
      timestamp: now - i * dayMs,
      score: Math.max(-100, Math.min(100, score)),
    });

    // Adjust score based on trend (going backwards)
    if (trend === 'improving') {
      score -= Math.floor(random() * 15) + 5; // Was lower before
    } else if (trend === 'declining') {
      score += Math.floor(random() * 15) + 5; // Was higher before
    } else {
      score += Math.floor(random() * 20) - 10; // Random fluctuation
    }
  }

  return scores;
}

// ============================================================================
// Cache Functions
// ============================================================================

/**
 * Get cached sentiment if valid
 */
function getCachedSentiment(tokenAddress: string): TokenSentiment | null {
  try {
    const stored = localStorage.getItem(`${SENTIMENT_CACHE_KEY}_${tokenAddress}`);
    if (!stored) return null;

    const cached: CachedSentiment = JSON.parse(stored);
    if (Date.now() - cached.cachedAt > SENTIMENT_CACHE_DURATION) {
      localStorage.removeItem(`${SENTIMENT_CACHE_KEY}_${tokenAddress}`);
      return null;
    }

    return cached.sentiment;
  } catch {
    return null;
  }
}

/**
 * Cache sentiment data
 */
function cacheSentiment(tokenAddress: string, sentiment: TokenSentiment): void {
  try {
    const cached: CachedSentiment = {
      sentiment,
      cachedAt: Date.now(),
    };
    localStorage.setItem(`${SENTIMENT_CACHE_KEY}_${tokenAddress}`, JSON.stringify(cached));
  } catch {
    // Ignore cache errors
  }
}

/**
 * Clear sentiment cache
 */
export function clearSentimentCache(): void {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(SENTIMENT_CACHE_KEY));
    keys.forEach(k => localStorage.removeItem(k));
  } catch {
    // Ignore errors
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get sentiment color
 */
export function getSentimentColor(score: number): string {
  if (score >= 50) return '#00ff88';
  if (score >= 20) return '#88ff88';
  if (score >= -20) return '#888888';
  if (score >= -50) return '#ff8888';
  return '#ff3b3b';
}

/**
 * Get sentiment label
 */
export function getSentimentLabel(score: number): string {
  if (score >= 50) return 'Very Bullish';
  if (score >= 20) return 'Bullish';
  if (score >= -20) return 'Neutral';
  if (score >= -50) return 'Bearish';
  return 'Very Bearish';
}

/**
 * Get trend icon
 */
export function getTrendIcon(trend: SentimentTrend): string {
  switch (trend) {
    case 'improving': return '\u2191'; // Up arrow
    case 'declining': return '\u2193'; // Down arrow
    case 'stable': return '\u2194'; // Left-right arrow
  }
}

/**
 * Get trend color
 */
export function getTrendColor(trend: SentimentTrend): string {
  switch (trend) {
    case 'improving': return '#00ff88';
    case 'declining': return '#ff3b3b';
    case 'stable': return '#888888';
  }
}

/**
 * Get source icon/label
 */
export function getSourceLabel(source: SocialMention['source']): string {
  switch (source) {
    case 'twitter': return 'X';
    case 'telegram': return 'TG';
    case 'discord': return 'Discord';
  }
}

/**
 * Format mention time
 */
export function formatMentionTime(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Simple hash function for consistent random generation
 */
function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

/**
 * Seeded random number generator
 */
function seededRandom(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

/**
 * Shuffle array with seeded random
 */
function shuffleArray<T>(array: T[], random: () => number): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}
